import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import Collapse from '@mui/material/Collapse';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import BarChartIcon from '@mui/icons-material/BarChart';
import PolicyIcon from '@mui/icons-material/Policy';

import NotificationsIcon from '@mui/icons-material/Notifications';
import AppsIcon from '@mui/icons-material/Apps';
import HealingIcon from '@mui/icons-material/Healing';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DnsIcon from '@mui/icons-material/Dns';
import TimelineIcon from '@mui/icons-material/Timeline';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ErrorIcon from '@mui/icons-material/Error';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import SpeedIcon from '@mui/icons-material/Speed';

import { teal } from '../../data/colors';
import { ProfileContext } from '../../data/auth-service';
import DataContext from '../../data/data-context';

import { get as _get } from 'lodash';

const drawerWidth = 300;
const drawerWidthCollapsed = '60px !important';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        backgroundColor: theme.palette.nav.paper,
        color: theme.palette.nav.text,
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        backgroundColor: theme.palette.nav.paper,
        // shawn gets confused by this and/or doesn't have a mouse wheel or trackpad
        // '&::-webkit-scrollbar': {
        //     display: 'none',
        // },
        // '-ms-overflow-style': 'none',
        // scrollbarWidth: 'none',
        transition: 'width 500ms, margin-left 500ms',
    },
    drawerCollapsed: {
        width: drawerWidthCollapsed,
        transition: 'width 500ms, margin-left 500ms',
    },
    drawerPaper: {
        width: drawerWidth,
        transition: 'width 500ms, margin-left 500ms',
        // '&::-webkit-scrollbar': {
        //     display: 'none',
        // },
        // '-ms-overflow-style': 'none',
        // scrollbarWidth: 'none',
        marginLeft: 300,
    },
    sidnavCollapsed: {
        marginLeft: 60,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(0, 1),
        ...theme.mixins.toolbar,
    },
    list: {
        flex: 1,
        width: '100%',
        minWidth: drawerWidth - 25,
        maxWidth: drawerWidth,
        paddingTop: 5,
        backgroundColor: theme.palette.nav.paper,
        color: theme.palette.nav.text,
        '& .MuiListItem-button': {
            borderBottom: `1px solid ${alpha(theme.palette.nav.text, 0.15)}`,
            paddingTop: '10px',
            paddingBottom: '10px',
            minHeight: 53,
            '& div': {
                color: theme.palette.nav.text,
            }
        },
        '& .MuiListItemText-secondary': {
            color: `${alpha(theme.palette.nav.text, 0.60)}`,
        },
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none'
    },
    widget: {
        whiteSpace: 'normal',
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        cursor: 'pointer',
    },
    widgetAvatar: {
        color: theme.palette.getContrastText(teal[400]),
        backgroundColor: teal[400],
    },
    widgetBottomSpacer: {
        height: '48px',
    },
}));

// keep keys as is so we can load up older dashboards
const WidgetTypes = {
    'brc-widget-alert-severity': {
        key: 'brc-widget-alert-severity',
        title: 'Alerts by Severity',
        icon: <DonutLargeIcon/>,
        description: 'View Alert Counts by Severity',
        perms: { Alerts: ['read'] },
    },
    'brc-widget-alert-group': {
        key: 'brc-widget-alert-group',
        title: 'Alerts by Group',
        icon: <DonutLargeIcon/>,
        description: 'View Alert Counts by Group',
        perms: { Alerts: ['read'] },
    },
    'brc-widget-alert-policy': {
        key: 'brc-widget-alert-policy',
        title: 'Alerts by Suricata Policy',
        icon: <PolicyIcon/>,
        description: 'View Suricata Policy Alert Statistics',
        requireAllPerms: true,
        perms: { Alerts: ['read'], SuricataRulesPolicy: ['read'] },
    },
    'brc-widget-active-alert-tags': {
        key: 'brc-widget-active-alert-tags',
        title: 'Alert Tags',
        icon: <BookmarkIcon/>,
        description: 'View most/least active alert tags',
        perms: { Alerts: ['read'] },
    },
    'brc-widget-alert-timeline': {
        key: 'brc-widget-alert-timeline',
        title: 'Alerts Timeline',
        icon: <BarChartIcon/>,
        description: 'View Alerts Timeline',
        perms: { Alerts: ['read'] },
    },
    'brc-widget-active-metadata-tags': {
        key: 'brc-widget-active-metadata-tags',
        title: 'Metadata Tags',
        icon: <BookmarkIcon/>,
        description: 'View most/least active metadata tags',
        perms: { Metadata: ['read'] },
    },
    'brc-widget-dns-activity': {
        key: 'brc-widget-dns-activity',
        title: 'Top DNS Queries',
        icon: <DnsIcon/>,
        description: 'View most active DNS queries',
        perms: { Metadata: ['read'] },
    },
    'brc-widget-mtdt-timeline': {
        key: 'brc-widget-mtdt-timeline',
        title: 'Metadata Timeline',
        icon: <TimelineIcon/>,
        description: 'View Metadata Activity Timeline',
        perms: { Metadata: ['read'] },
    },
    'brc-widget-mtdt-ip-activity': {
        key: 'brc-widget-mtdt-ip-activity',
        title: 'IP Activity Flow',
        icon: <AcUnitIcon/>,
        description: 'View Activity for IP Address',
        perms: { Metadata: ['read'] },
    },
    'brc-widget-health-alerts': {
        key: 'brc-widget-health-alerts',
        title: 'Health Alerts',
        icon: <ErrorIcon/>,
        description: 'View latest health alerts for CMC and Sensors',
        perms: { SystemHealth: ['read'] },
    },
    'brc-widget-mtdt-group': {
        key: 'brc-widget-mtdt-group',
        title: 'Metadata by Group',
        icon: <DonutLargeIcon/>,
        description: 'View Metadata Activity Counts by Group',
        perms: { Metadata: ['read'] },
    },
    'brc-widget-user-agents': {
        key: 'brc-widget-user-agents',
        title: 'User Agents',
        icon: <DonutSmallIcon/>,
        description: 'View User Agent Metadata',
        perms: { Metadata: ['read'] },
    },
    'brc-widget-health-metric': {
        key: 'brc-widget-health-metric',
        title: 'Health Metric',
        icon: <SpeedIcon/>,
        description: 'View health metric for Sensor or CMC',
        perms: { SystemHealth: ['read'], Sensors: ['read'] },
    },
    // mc2 widgets
    'brc-widget-mc2-health-alerts': {
        key: 'brc-widget-mc2-health-alerts',
        title: 'Health Alerts',
        icon: <ErrorIcon/>,
        description: 'View latest health alerts for CMC and Sensors',
        configurator: 'brcWidgetHealthAlertsConfig',
        perms: { SystemHealth: ['read'], CMC: ['read'] },
    },
    'brc-widget-mc2-health-metric': {
        key: 'brc-widget-mc2-health-metric',
        title: 'Health Metric',
        icon: <SpeedIcon/>,
        description: 'View health metric for Sensors, CMCs or MC2 Health',
        configurator: 'brcWidgetHealthMetricConfig',
        perms: { SystemHealth: ['read'], CMC: ['read'] },
    },
    'brc-widget-mc2-alert-severity': {
        key: 'brc-widget-mc2-alert-severity',
        title: 'Alerts by Severity',
        icon: <DonutLargeIcon/>,
        description: 'View CMC Alert Counts by Severity',
        configurator: 'brcWidgetAlertSeverityConfig',
        perms: { Alerts: ['read'] },
    },
    'brc-widget-mc2-alert-counts': {
        key: 'brc-widget-mc2-alert-counts',
        title: 'Alert Counts',
        icon: <DonutLargeIcon/>,
        description: 'View CMC Alert Counts for provided filters',
        configurator: 'brcWidgetCountsConfig',
        perms: { Alerts: ['read'] },
    },
    'brc-widget-mc2-mtdt-counts': {
        key: 'brc-widget-mc2-mtdt-counts',
        title: 'Metadata Counts',
        icon: <DonutLargeIcon/>,
        description: 'View CMC Metadata Counts for provided filters',
        configurator: 'brcWidgetCountsConfig',
        perms: { Metadata: ['read'] },
    },
    'brc-widget-mc2-mtdt-timeline': {
        key: 'brc-widget-mc2-mtdt-timeline',
        title: 'Metadata Timeline',
        icon: <TimelineIcon/>,
        description: 'View CMC Metadata Activity Timeline',
        configurator: 'brcWidgetMtdtTimelineConfig',
        perms: { Metadata: ['read'] },
    },
};

const CMCWidgets = {
    Alerts: {
        icon: <NotificationsIcon/>,
        widgets: [
            WidgetTypes['brc-widget-alert-severity'],
            WidgetTypes['brc-widget-alert-group'],
            WidgetTypes['brc-widget-alert-policy'],
            WidgetTypes['brc-widget-active-alert-tags'],
            WidgetTypes['brc-widget-alert-timeline'],
        ]
    },
    Metadata: {
        icon: <AppsIcon/>,
        widgets: [
            WidgetTypes['brc-widget-mtdt-group'],
            WidgetTypes['brc-widget-active-metadata-tags'],
            WidgetTypes['brc-widget-dns-activity'],
            WidgetTypes['brc-widget-mtdt-timeline'],
            WidgetTypes['brc-widget-mtdt-ip-activity'],
            WidgetTypes['brc-widget-user-agents'],
        ]
    },
    Health: {
        icon: <HealingIcon/>,
        widgets: [
            WidgetTypes['brc-widget-health-alerts'],
            WidgetTypes['brc-widget-health-metric'],
        ]
    },
};

const MC2Widgets = {
    Alerts: {
        icon: <NotificationsIcon/>,
        widgets: [
            WidgetTypes['brc-widget-mc2-alert-severity'],
            WidgetTypes['brc-widget-mc2-alert-counts'],
        ]
    },
    Metadata: {
        icon: <AppsIcon/>,
        widgets: [
            WidgetTypes['brc-widget-mc2-mtdt-counts'],
            WidgetTypes['brc-widget-mc2-mtdt-timeline'],
        ]
    },
    Health: {
        icon: <HealingIcon/>,
        widgets: [
            WidgetTypes['brc-widget-mc2-health-alerts'],
            WidgetTypes['brc-widget-mc2-health-metric'],
        ]
    },
};

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

const WidgetsSidebar = (props) => {
    const classes = useStyles();

    const { perm } = useContext(ProfileContext);
    const { systemSettings, sidenav, setSidenav } = useContext(DataContext);
    const [header, setHeader] = useState(false);
    const [init, setInit] = useState(false);

    // keeping widget type attributes that are needed for the angular app to still work
    const [types, setTypes] = useState(
        Object.entries(MC2CHECK ? MC2Widgets : CMCWidgets)
            .reduce((a, [k, v]) => {
                // allow match on any perm. granular perms are checked in the widget
                // unless widget requireAllPerms is set to true
                v.widgets = v.widgets.filter(w => {
                    return w.requireAllPerms
                        ? !Object.entries(w.perms).some(([k, v]) => !perm({ [k]: v }))
                        : perm(w.perms);
                });
                return v.widgets.length > 0 ? { ...a, [k]: v } : a;
            }, {}));

    const handleOpen = open => {
        props.onToggleClose(open);
        // collapse all types when closing the panel
        setTypes(current => {
            return Object.entries(current).reduce((a, [k, v]) => {
                return { ...a, [k]: { ...v, expanded: false } };
            }, {});
        });
    };

    const handleToggleCategory = (e, c) => {
        e.stopPropagation();
        setTypes({
            ...types,
            [c]: { ...types[c], expanded: !types[c].expanded },
        });
        props.onToggleClose(true);
    };

    // dashboard selection hook
    useEffect(() => {
        setInit(true);
        setSidenav({ collapsed: true, sidebar: 'collapsed' });

        return () => {
            setSidenav({ sidebar: undefined });
        };
    }, []);

    useEffect(() => {
        // we want to start the page with collapsed panels
        if (!init) return;
        setSidenav({ sidebar: props.open ? 'open' : 'collapsed' });
    }, [props.open]);

    useEffect(() => {
        setHeader(!!_get(systemSettings, 'ui.messages.header'));
    }, [systemSettings]);

    return (
        <div className={classes.root}>
            <Drawer
                open={true}
                variant="persistent"
                classes={{
                    paper: clsx(classes.drawerPaper, {
                        [classes.drawerCollapsed]: sidenav.sidebar === 'collapsed',
                        [classes.sidnavCollapsed]: sidenav.collapsed,
                    }),
                }}>
                <div className={classes.toolbar} />
                <List
                    style={{ marginTop: header ? 45 : 15 }}
                    component="nav"
                    className={classes.list}
                    aria-label="Widget Types">
                    <ListItem button
                        onClick={() => { handleOpen(sidenav.sidebar !== 'open') }}
                        aria-label="Expand or Collpase Widget Types">
                        <ListItemIcon>
                            {sidenav.sidebar === 'open' ? <NavigateBeforeIcon /> : <NavigateNextIcon/>}
                        </ListItemIcon>
                    </ListItem>
                    {Object.entries(types).map(([k, v]) => (
                        <React.Fragment key={k}>
                            <ListItem
                                className={classes.listItem}
                                button
                                onClick={e => handleToggleCategory(e, k)}
                                aria-label={k}>
                                <ListItemIcon>
                                    {React.cloneElement(v.icon)}
                                </ListItemIcon>
                                <ListItemText
                                    style={{ display: (sidenav.sidebar === 'open' ? 'block' : 'none') }}
                                    primary={`${k} Widgets`}>
                                </ListItemText>
                                {v.expanded
                                    ? <ExpandLessIcon
                                        onClick={e => handleToggleCategory(e, k)}
                                        aria-label={`Collpase ${k}`} />
                                    : <ExpandMoreIcon
                                        onClick={e => handleToggleCategory(e, k)}
                                        aria-label={`Expand ${k}`} />
                                }
                            </ListItem>
                            <Collapse in={v.expanded} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {v.widgets.map((t, i) =>
                                        <ListItem
                                            key={t.key}
                                            className={classes.widget}
                                            onClick={() => props.onAddWidget(t)}>
                                            <ListItemAvatar>
                                                <Avatar className={classes.widgetAvatar}>
                                                    {React.cloneElement(t.icon)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={t.title}
                                                secondary={t.description} />
                                        </ListItem>
                                    )}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    ))}
                    <ListItem className={classes.widgetBottomSpacer}></ListItem>
                </List>
            </Drawer>
        </div>
    );
};

WidgetsSidebar.propTypes = {
    open: PropTypes.bool,
    onToggleClose: PropTypes.func,
    onAddWidget: PropTypes.func.isRequired,
    perm: PropTypes.func,
};

export {
    WidgetsSidebar as default,
    WidgetTypes,
};
